import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Button,
  Chip,
  Spinner,
  Table,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { RefreshIcon } from '@enterprise-ui/icons'
import { ErrorSummaryModal } from '../../globalComponents/ErrorSummaryModal'
import { getSpreadsheetHistory } from '../../services/requests'
import {
  TOASTER_DEFAULTS,
  SPREADSHEET_HISTORY_ERROR_SUMMARY_FIELDS,
  dateTimeToString,
} from '../../globalConfig/constants'

export const SpreadsheetHistoryTable = ({
  vendorId,
  successfulUploadCount,
}) => {
  const [waitingOnResponse, setWaitingOnResponse] = useState(false)
  const [historyData, setHistoryData] = useState([])
  const [errorSummaryFileName, setErrorSummaryFileName] = useState(null)
  const [errorSummaryData, setErrorSummaryData] = useState(null)
  const makeToast = useToaster()
  const isMounted = useRef(false)

  useEffect(() => {
    makeSpreadsheetHistoryRequest(vendorId)
    // I don't want to make a habit of disabling these lint checks but I feel it
    // makes the most sense in this scenario because the recommended fix of
    // declaring the makeSpreadsheetHistoryRequest function inside the useEffect
    // would result in a lot of duplicate code
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId])

  useEffect(() => {
    if (successfulUploadCount) {
      refreshTableData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfulUploadCount])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const makeSpreadsheetHistoryRequest = (vendor) => {
    setWaitingOnResponse(true)
    getSpreadsheetHistory(vendor)
      .then((response) => {
        setWaitingOnResponse(false)
        if (response?.status === 200) {
          if (isMounted?.current) {
            setHistoryData(buildTableData(response?.data?.workbookData))
          }
        } else {
          setWaitingOnResponse(false)
          sendFailureToast()
        }
      })
      .catch(() => {
        setWaitingOnResponse(false)
        sendFailureToast()
      })
  }

  const buildTableData = (dataArray) => {
    const returnArray = []

    dataArray.forEach((row) => {
      const displayRow = {}
      displayRow.file_name = {
        cellValue: row?.fileName,
        cellDisplay: <div className="hesp-sd-historyRow">{row?.fileName}</div>,
      }
      displayRow.submitted_by = {
        cellValue: row?.submittedBy,
        cellDisplay: (
          <div className="hesp-sd-historyRow">{row?.submittedBy}</div>
        ),
      }
      displayRow.upload_date = {
        cellValue: row?.uploadDate,
        cellDisplay: (
          <div className="hesp-sd-historyRow">
            {dateTimeToString(row?.uploadDate)}
          </div>
        ),
      }
      displayRow.status = {
        cellValue: row?.status,
        cellDisplay: (
          <div className="hesp-sd-historyRow">
            <Chip
              data-testid="history-table-status-chip"
              className="hesp-general-marginNone"
              size="expanded"
              color={
                row?.status === 'FAILED'
                  ? 'error'
                  : row?.status === 'SUCCESS'
                    ? 'success'
                    : 'alert'
              }
            >
              <span className="hc-fs-sm">
                <strong>{row?.status}</strong>
              </span>
            </Chip>
          </div>
        ),
      }
      displayRow.total_count = {
        cellValue: row?.rowCount,
        cellDisplay: (
          <div className="hesp-sd-historyRow">
            <Chip
              data-testid="history-table-total-count-chip"
              className="hesp-general-marginNone hesp-general-backgroundContrast"
              size="expanded"
            >
              <span className="hesp-chip-plain">
                <strong>{row?.rowCount || 0}</strong>
              </span>
            </Chip>
          </div>
        ),
      }
      displayRow.error_count = {
        cellValue: row?.errorCount,
        cellDisplay: (
          <div className="hesp-sd-historyRow">
            <Chip
              data-testid="history-table-error-count-chip"
              className="hesp-general-marginNone"
              size="expanded"
              color={row?.errorCount ? 'error' : 'success'}
              clickable={Boolean(row?.errorCount && row?.errors?.length)}
              onClick={() => {
                setErrorSummaryFileName(row?.fileName)
                setErrorSummaryData(
                  row?.errors.map((errorObj) => ({
                    ...errorObj,
                    [SPREADSHEET_HISTORY_ERROR_SUMMARY_FIELDS
                      ?.COST_LOCATION_GROUP_TYPE?.variableName]:
                      row?.[
                        SPREADSHEET_HISTORY_ERROR_SUMMARY_FIELDS
                          ?.COST_LOCATION_GROUP_TYPE?.variableName
                      ],
                  })),
                )
              }}
            >
              <span className="hesp-chip-plain">
                <strong>{row?.errorCount || 0}</strong>
              </span>
            </Chip>
          </div>
        ),
      }
      returnArray.push(displayRow)
    })
    return returnArray
  }

  const sendFailureToast = () => {
    makeToast({
      ...TOASTER_DEFAULTS,
      type: 'error',
      heading: 'Error',
      message: 'Failed to get spreadsheet history data.',
    })
  }

  const refreshTableData = () => {
    makeSpreadsheetHistoryRequest(vendorId)
  }

  const onErrorSummaryClose = useCallback(() => {
    setErrorSummaryFileName(null)
    setErrorSummaryData(null)
  }, [])

  return (
    <div className="hesp-position-relative hesp-spreadsheet-history-table-safari">
      {waitingOnResponse ? (
        <>
          <span
            data-testid="history-table-upload-lockout"
            className="hesp-lockout-container hesp-lockout-overlay"
          ></span>
          <span className={'hesp-lockout-container'}>
            <Spinner size="expanded" />
          </span>
        </>
      ) : null}
      <Table
        className={`hesp-table-header hesp-spreadsheet-history-table${!historyData?.length ? ' hesp-table-empty' : ''}`}
        style={{ minHeight: '400px' }}
        data-testid="sh-table"
        name="Spreadsheet History"
        id="spreadsheet-history"
        cellPadding="dense"
        height={400}
        fixedHeader
        data={{
          defaultColDef: { sortable: false, resizable: true },
          columnDefs: [
            {
              field: 'file_name',
              headerName: 'File Name',
              isRowHeader: true,
              align: 'center',
              width: 300,
            },
            {
              field: 'submitted_by',
              headerName: 'Submitted By',
              align: 'center',
              width: 200,
            },
            {
              field: 'upload_date',
              headerName: 'Upload Date',
              align: 'center',
              width: 225,
            },
            {
              field: 'status',
              headerName: 'Status',
              align: 'center',
              width: 200,
            },
            {
              field: 'total_count',
              headerName: 'Total Rows',
              align: 'center',
              width: 125,
            },
            {
              field: 'error_count',
              headerName: 'Errored Rows',
              align: 'center',
              width: 125,
            },
          ],
          rowData: historyData?.length ? historyData : [{}],
        }}
        roundCorner
        pagination={{
          type: 'client',
          pageSizeOptions: [5, 10, 25, 50],
          initialPageSize: 10,
          hidePageSize: !historyData.length,
          hidePaginationNavigation: !historyData.length,
          hideShowItems: !historyData.length,
        }}
      />
      {!historyData.length && !waitingOnResponse ? (
        <span
          style={{
            position: 'relative',
            display: 'inline-block',
            width: '100%',
            textAlign: 'center',
            bottom: '200px',
          }}
        >
          {'No results found'}
        </span>
      ) : null}
      <Button
        data-testid="sh-refresh-button"
        style={{
          position: 'absolute',
          right: 0,
          top: -40,
          color: 'var(--canvas-font-color--default)',
        }}
        type="ghost"
        onClick={refreshTableData}
        disabled={waitingOnResponse}
      >
        <EnterpriseIcon className="hc-mr-dense" icon={RefreshIcon} />
        REFRESH
      </Button>
      <ErrorSummaryModal
        isVisible={Boolean(errorSummaryData)}
        tableData={errorSummaryData}
        headerMetadata={SPREADSHEET_HISTORY_ERROR_SUMMARY_FIELDS}
        rowHeader={SPREADSHEET_HISTORY_ERROR_SUMMARY_FIELDS.TCIN}
        headingText={`Error Summary - ${errorSummaryFileName}`}
        onClose={onErrorSummaryClose}
      />
    </div>
  )
}
